/* ENEA: bring infinite scroll back when a lazy load dies (2026-08-29)
 *
 * SYMPTOM the owner reported: open a product from a collection while more products are
 * still loading, come back, and the collection sits on "Loading" forever. Only a full
 * refresh clears it. He was on a poor connection at the time.
 *
 * CAUSE, read out of the Ajaxinate library in assets/vendor.js and confirmed in the
 * browser on 2026-08-29. Two faults combine:
 *
 *   checkIfPaginationInView() calls removeScrollListener() UNCONDITIONALLY, before it has
 *   done anything else. It then writes "Loading" into the link and starts the request.
 *
 *   loadMore() has no failure handling at all. There is no onerror, no ontimeout and no
 *   onabort anywhere in it. Its only handler is onreadystatechange, and that returns on
 *   its first line when responseXML is empty, which is exactly what a failed or aborted
 *   request gives you. initialize(), the one thing that puts the scroll listener back,
 *   sits inside the success branch.
 *
 * So a single failed request is fatal: the listener is already gone, the link still says
 * "Loading", and nothing will ever retry. Reproduced by aborting an in-flight batch, after
 * which no amount of scrolling brought it back.
 *
 * Why it hits him and not us: each batch of this collection is about 1.5MB, which is
 * roughly thirty seconds on a 3G connection. That is a wide window for a request to fail
 * in, and a wide window to tap a product during.
 *
 * WHAT THIS DOES: watches the pagination element. If it has been sitting in its loading
 * state for STUCK_MS without a single new product arriving, it assumes the library is
 * dead, puts the link back, fetches the page itself, and takes over the scroll listener so
 * later batches keep working. It never fights a load that is merely slow, because a slow
 * load still grows the grid, and the timer only counts time with no progress at all.
 *
 * Appends are de-duplicated by card key, so it is harmless if the original request turns
 * out to have succeeded after all, and harmless alongside snippets/enea-collection-return
 * which pulls its own pages during a back navigation.
 *
 * TO REMOVE: delete this file and its script tag in snippets/loading.liquid.
 */
(function () {
  'use strict';

  var STUCK_MS = 10000; /* no new products for this long while loading = dead */
  var POLL_MS = 1000;
  var MAX_PAGES = 40; /* hard stop so a takeover can never run away */

  function loopEl() {
    return document.getElementById('AjaxinateLoop');
  }

  function pagEl() {
    return document.getElementById('AjaxinatePagination');
  }

  function anchorEl() {
    var p = pagEl();
    return p ? p.querySelector('a[href]') : null;
  }

  function cardCount() {
    var l = loopEl();
    return l ? l.querySelectorAll('.product-grid-item').length : 0;
  }

  /* Same key the back-navigation snippet uses: the card id with the render timestamp
     stripped, so the same product rendered twice is recognised as one card. */
  function cardKey(node) {
    if (!node || !node.id) return null;
    var stripped = node.id.replace(/--\d+$/, '');
    return stripped === node.id ? null : stripped;
  }

  /* Once we take over, the library may still append the same page we just pulled, and its
     own append never checks for repeats. Sweep after any growth so the grid stays clean. */
  function dedupeGrid() {
    var l = loopEl();
    if (!l) return 0;
    var seen = {}, removed = 0;
    var cards = l.querySelectorAll('.product-grid-item[id]');
    for (var i = 0; i < cards.length; i++) {
      var k = cardKey(cards[i]);
      if (!k) continue;
      if (seen[k]) { cards[i].parentNode.removeChild(cards[i]); removed++; }
      else { seen[k] = 1; }
    }
    return removed;
  }

  function presentKeys() {
    var seen = {};
    var l = loopEl();
    if (!l) return seen;
    var cards = l.querySelectorAll('.product-grid-item[id]');
    for (var i = 0; i < cards.length; i++) {
      var k = cardKey(cards[i]);
      if (k) seen[k] = 1;
    }
    return seen;
  }

  if (!loopEl() || !pagEl()) return;

  /* The label to put back. Captured before anything can overwrite it with "Loading",
     rather than assuming the English default, since the theme can translate it. */
  var originalLabel = (function () {
    var a = anchorEl();
    var text = a ? a.textContent.replace(/\s+/g, ' ').trim() : '';
    return text || null;
  })();

  var takenOver = false;
  var busy = false;
  var pagesPulled = 0;

  function looksLikeLoading() {
    var a = anchorEl();
    if (!a) return false;
    var text = a.textContent.replace(/\s+/g, ' ').trim();
    return !!text && text !== originalLabel;
  }

  /* The library can also die before it writes its label at all, which leaves the loader
     graphic parked under a full grid with nothing ever arriving. That counts as stuck. */
  function idleUnderPagination() {
    var pag = pagEl();
    var a = anchorEl();
    if (!pag || !a || !a.getAttribute('href')) return false;
    var r = pag.getBoundingClientRect();
    return r.top <= window.innerHeight && r.bottom >= 0;
  }

  function appendFrom(incoming) {
    var l = loopEl();
    if (!l || !incoming) return 0;

    var seen = presentKeys();
    var frag = document.createDocumentFragment();
    var added = 0;

    for (var i = 0; i < incoming.children.length; i++) {
      var node = incoming.children[i];
      /* importNode runs a script tag once it is inserted. Grid batches should never carry
         one, but never import markup that could execute. */
      if (node.tagName === 'SCRIPT' || node.querySelector('script')) continue;

      var k = node.id ? cardKey(node) : null;
      if (k && seen[k]) continue; /* already on the page, from the library or the restore */
      if (k) seen[k] = 1;

      frag.appendChild(document.importNode(node, true));
      added++;
    }

    l.appendChild(frag);
    return added;
  }

  /* Do the load the library failed to do, and update the link so the next one can happen. */
  function pull() {
    if (busy) return;
    var a = anchorEl();
    if (!a) return;

    var url = a.getAttribute('href');
    if (!url || pagesPulled >= MAX_PAGES) return;

    busy = true;
    pagesPulled++;

    fetch(url, {credentials: 'same-origin'})
      .then(function (r) {
        return r && r.ok ? r.text() : null;
      })
      .then(function (html) {
        if (!html) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        appendFrom(doc.getElementById('AjaxinateLoop'));

        var pag = pagEl();
        var incomingPag = doc.getElementById('AjaxinatePagination');
        if (pag) {
          /* No next page in the response means the collection is finished. Emptying it is
             what the library does too, and it stops anything asking again. */
          pag.innerHTML = incomingPag ? incomingPag.innerHTML : '';
        }
      })
      .catch(function () {
        /* Leave the link as it is. The next scroll will try again, which is the whole
           point: a failure here is recoverable, unlike the one this file exists for. */
      })
      .then(function () {
        busy = false;
        dedupeGrid();
        lastCount = cardCount();
        stuckSince = 0;
      });
  }

  /* Once the library is dead its scroll listener is gone for good, so carry the job. */
  function onScroll() {
    if (busy) return;
    var pag = pagEl();
    var a = anchorEl();
    if (!pag || !a) return;

    var rect = pag.getBoundingClientRect();
    if (rect.top <= window.innerHeight && rect.bottom >= 0) pull();
  }

  function takeOver() {
    if (takenOver) return;
    takenOver = true;

    var a = anchorEl();
    if (a && originalLabel) a.textContent = originalLabel;

    window.addEventListener('scroll', onScroll, {passive: true});
    window.addEventListener('resize', onScroll, {passive: true});

    pull();
  }

  var lastCount = cardCount();
  var stuckSince = 0;

  setInterval(function () {
    var count = cardCount();

    if (count !== lastCount) {
      /* The grid grew, so whoever is loading is alive. A slow batch is not a stuck one. */
      if (takenOver) dedupeGrid();
      lastCount = cardCount();
      stuckSince = 0;
      return;
    }

    if (takenOver || !(looksLikeLoading() || idleUnderPagination())) {
      stuckSince = 0;
      return;
    }

    if (!stuckSince) {
      stuckSince = Date.now();
      return;
    }

    if (Date.now() - stuckSince >= STUCK_MS) takeOver();
  }, POLL_MS);
})();
